import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DollarSign, FileText, Clock, CheckCircle, ArrowDownCircle, ArrowUpCircle, CalendarClock, TrendingUp, Calendar } from "lucide-react";
import { FinanceiroTable } from "./FinanceiroTable";
import { useFinanceiroStats } from "@/hooks/useFinanceiroStats";
import { ProximosVencimentos } from "./ProximosVencimentos";
import { useAuth } from "@/hooks/useAuth";
import { useUltimasTransacoesFinanceiras } from "@/hooks/useFinanceiro";
import {
  buildFinanceiroFiltroParams,
  getFinanceiroFiltrosDraftDefault,
  getInicioFimMesAtual,
} from "@/lib/financeiroFiltros";

export function Financeiro() {
  const [abaFinanceiro, setAbaFinanceiro] = useState<"fluxo" | "receber" | "pagar">("fluxo");
  const [periodoFluxo, setPeriodoFluxo] = useState(() => getInicioFimMesAtual());
  const filtrosDraftDefault = useMemo(() => getFinanceiroFiltrosDraftDefault(), []);
  const [filtrosDraft, setFiltrosDraft] = useState(filtrosDraftDefault);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosDraftDefault);
  const filtrosHook = useMemo(
    () => buildFinanceiroFiltroParams(filtrosAplicados),
    [filtrosAplicados]
  );
  const filtrosParaCards = useMemo(() => {
    if (abaFinanceiro === "fluxo") {
      return {
        vencimentoPeriodo: periodoFluxo,
        pagamentoPeriodo: periodoFluxo,
        valorMin: null,
        valorMax: null,
        formaPagamento: null,
        parcelas: null,
        estado: null,
        cidade: null,
        status: null,
      };
    }
    return filtrosHook;
  }, [abaFinanceiro, periodoFluxo, filtrosHook]);
  const { canAccessContasAPagar, isVendedorInterno, isVendedorScoped, user } = useAuth();
  const showContasAPagar = canAccessContasAPagar();
  /** Vendedor interno: escopo próprio, mas pode lançar/confirmar contas a receber */
  const escopoVendedor = isVendedorScoped() || isVendedorInterno();
  const vendedorIdEscopo = escopoVendedor && user?.id ? user.id : null;
  const { data: ultimasTransacoes = [] } = useUltimasTransacoesFinanceiras(15, showContasAPagar);

  const filtrosParaCardsEscopo = useMemo(() => {
    if (!vendedorIdEscopo) return filtrosParaCards;
    return { ...filtrosParaCards, vendedorId: vendedorIdEscopo };
  }, [filtrosParaCards, vendedorIdEscopo]);

  const stats = useFinanceiroStats(
    filtrosParaCardsEscopo,
    abaFinanceiro === "fluxo" ? periodoFluxo : null
  );

  const isTransacaoVencida = (transacao: any) => {
    if (transacao.status !== "pendente" || !transacao.data_vencimento) return false;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const vencimento = new Date(`${transacao.data_vencimento}T00:00:00`);
    return vencimento < hoje;
  };

  const getStatusBadgeClass = (transacao: any) => {
    if (isTransacaoVencida(transacao)) return "bg-red-100 text-red-800";
    if (transacao.status === "pago") return "bg-green-100 text-green-800";
    if (transacao.status === "cancelado") return "bg-red-100 text-red-800";
    return "bg-yellow-100 text-yellow-800";
  };

  const getStatusLabel = (transacao: any) => isTransacaoVencida(transacao) ? "vencido" : transacao.status;

  const formatarData = (data?: string) => data ? new Date(`${data}T00:00:00`).toLocaleDateString("pt-BR") : "-";
  const formatarValor = (valor: number) => `R$ ${Number(valor || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Financeiro</h2>
        <p className="text-gray-600">
          {showContasAPagar ? "Controle financeiro, contas a receber, a pagar e fluxo de caixa" : "Contas a receber"}
        </p>
      </div>

      {/* Cards do financeiro com fundo cinza */}
      <div className="bg-[#C9C9C9] rounded-lg p-6 space-y-4">
        <div
          className={
            showContasAPagar
              ? "grid grid-cols-1 md:grid-cols-4 gap-4"
              : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
          }
        >
        <Card className="border-0 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Receita Mensal</CardTitle>
            <DollarSign className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              R$ {stats.receitaMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className={`text-xs ${stats.percentualCrescimento >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.mostrarCrescimento
                ? `${stats.percentualCrescimento >= 0 ? '+' : ''}${stats.percentualCrescimento.toFixed(1)}% em relação ao mês anterior`
                : `Recebidos no período: ${stats.periodoPagamentoLabel}`}
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Pagamentos Pendentes</CardTitle>
            <Clock className="h-5 w-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              R$ {stats.pagamentosPendentes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-gray-600">
              {stats.totalClientesPendentes} cliente{stats.totalClientesPendentes !== 1 ? 's' : ''} pendente{stats.totalClientesPendentes !== 1 ? 's' : ''}
              {stats.temFiltroAtivo ? ` • Venc.: ${stats.periodoVencimentoLabel}` : ''}
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Em Atraso</CardTitle>
            <CalendarClock className="h-5 w-5 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              R$ {stats.pagamentosEmAtraso.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-red-600">{stats.totalClientesEmAtraso} cliente{stats.totalClientesEmAtraso !== 1 ? 's' : ''} em atraso</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Pagos</CardTitle>
            <CheckCircle className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              R$ {stats.pagamentosPagos.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-green-600">
              {stats.totalPagamentosRealizados} pagamento{stats.totalPagamentosRealizados !== 1 ? 's' : ''} no período
              {stats.temFiltroAtivo ? ` (${stats.periodoPagamentoLabel})` : ''}
            </p>
          </CardContent>
        </Card>

        {!showContasAPagar && (
          <Card className="border-0 shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Orçamentos aprovados</CardTitle>
              <DollarSign className="h-5 w-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                R$ {stats.orcamentosAprovadosValor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-gray-600">
                Previsto (pipeline){stats.temFiltroAtivo ? ` • ${stats.periodoVencimentoLabel}` : ''}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {showContasAPagar && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-0 shadow-md">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Despesas pendentes</CardTitle>
                <ArrowUpCircle className="h-5 w-5 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">
                  R$ {stats.despesasPendentes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-gray-600">Contas a pagar</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Despesas pagas (mês)</CardTitle>
                <FileText className="h-5 w-5 text-gray-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">
                  R$ {stats.totalDespesasMes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-gray-600">
                  {stats.temFiltroAtivo ? `Saídas pagas: ${stats.periodoPagamentoLabel}` : 'Saídas pagas no mês'}
                </p>
              </CardContent>
            </Card>
        <Card className="border-0 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Orçamentos aprovados</CardTitle>
            <DollarSign className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              R$ {stats.orcamentosAprovadosValor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-gray-600">
              Previsto (pipeline){stats.temFiltroAtivo ? ` • ${stats.periodoVencimentoLabel}` : ''}
            </p>
          </CardContent>
        </Card>
      </div>
      )}
      </div>

      {/* Abas: para Operador só "A receber"; para os demais "A receber" | "A pagar" | "Fluxo de caixa" */}
      {showContasAPagar ? (
        <Tabs value={abaFinanceiro} onValueChange={(v) => setAbaFinanceiro(v as "fluxo" | "receber" | "pagar")} className="w-full">
          <TabsList className="grid w-full max-w-2xl grid-cols-3">
            <TabsTrigger value="fluxo" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              Fluxo de caixa
            </TabsTrigger>
            <TabsTrigger value="receber" className="flex items-center gap-2">
              <ArrowDownCircle className="h-4 w-4 text-green-600" />
              A receber
            </TabsTrigger>
            <TabsTrigger value="pagar" className="flex items-center gap-2">
              <ArrowUpCircle className="h-4 w-4 text-red-600" />
              A pagar
            </TabsTrigger>
          </TabsList>

          <TabsContent value="fluxo" className="space-y-6 mt-4">
            <Card className="border-0 shadow-lg bg-gradient-to-r from-gray-50 to-blue-50">
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-blue-600" />
                      Fluxo de caixa
                    </CardTitle>
                    <p className="text-sm text-gray-600 mt-1">
                      {(() => {
                        const mesAtual = getInicioFimMesAtual();
                        const ehMesAtual = periodoFluxo.dataInicio === mesAtual.dataInicio && periodoFluxo.dataFim === mesAtual.dataFim;
                        return ehMesAtual
                          ? "Resumo de entradas e saídas (mês atual)"
                          : `Resumo do período ${new Date(periodoFluxo.dataInicio + "T12:00:00").toLocaleDateString("pt-BR")} a ${new Date(periodoFluxo.dataFim + "T12:00:00").toLocaleDateString("pt-BR")}`;
                      })()}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <input
                        type="date"
                        value={periodoFluxo.dataInicio}
                        onChange={(e) => setPeriodoFluxo((p) => ({ ...p, dataInicio: e.target.value }))}
                        className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                      />
                      <span className="text-gray-500">até</span>
                      <input
                        type="date"
                        value={periodoFluxo.dataFim}
                        onChange={(e) => setPeriodoFluxo((p) => ({ ...p, dataFim: e.target.value }))}
                        className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setPeriodoFluxo(getInicioFimMesAtual())}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Mês atual
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-green-100">
                    <p className="text-sm font-medium text-gray-600">Entradas (recebidas)</p>
                    <p className="text-2xl font-bold text-green-600">
                      R$ {stats.receitaMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-gray-500">Receita do período</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-red-100">
                    <p className="text-sm font-medium text-gray-600">Saídas (pagas no período)</p>
                    <p className="text-2xl font-bold text-red-600">
                      R$ {stats.totalDespesasMes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-gray-500">Despesas pagas no período</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-blue-200">
                    <p className="text-sm font-medium text-gray-600">Saldo do período</p>
                    <p className={`text-2xl font-bold ${(stats.receitaMensal - stats.totalDespesasMes) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      R$ {(stats.receitaMensal - stats.totalDespesasMes).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-gray-500">Entradas − Saídas</p>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    Pendentes a receber: R$ {stats.pagamentosPendentes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} •
                    Em atraso: R$ {stats.pagamentosEmAtraso.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} •
                    Despesas pendentes: R$ {stats.despesasPendentes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </CardContent>
            </Card>

            <ProximosVencimentos vendedorId={vendedorIdEscopo} />

            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-gray-900">Últimas 15 transações financeiras</CardTitle>
              </CardHeader>
              <CardContent>
                {ultimasTransacoes.length === 0 ? (
                  <div className="text-center text-gray-500 py-6">
                    Nenhuma transação encontrada
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ultimasTransacoes.map((transacao) => {
                      const nome = transacao.fornecedor?.nome
                        ?? transacao.cliente?.nome
                        ?? (transacao.fornecedor_id
                          ? `Fornecedor ID: ${transacao.fornecedor_id}`
                          : transacao.cliente_id
                            ? `Cliente ID: ${transacao.cliente_id}`
                            : "—");

                      return (
                        <div
                          key={transacao.id}
                          className={`flex flex-col gap-2 rounded-lg border p-3 md:flex-row md:items-center md:justify-between ${
                            isTransacaoVencida(transacao) ? "border-red-200 bg-red-50" : "bg-white"
                          }`}
                        >
                          <div className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-gray-900">#{transacao.id}</span>
                              <Badge className={transacao.tipo === "entrada" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                                {transacao.tipo === "entrada" ? "Entrada" : "Saída"}
                              </Badge>
                              <Badge className={getStatusBadgeClass(transacao)}>
                                {getStatusLabel(transacao)}
                              </Badge>
                            </div>
                            <div className="text-sm text-gray-900">{nome}</div>
                            <div className="text-xs text-gray-500">
                              Forma: {transacao.forma_pagamento} • Vencimento:{" "}
                              <span className={isTransacaoVencida(transacao) ? "font-medium text-red-700" : ""}>
                                {formatarData(transacao.data_vencimento)}
                              </span>
                              {" • "}Atualizado em: {formatarData(transacao.updated_at?.split("T")[0])}
                            </div>
                          </div>
                          <div className={`text-right font-semibold ${transacao.tipo === "entrada" ? "text-green-700" : "text-red-700"}`}>
                            {formatarValor(transacao.valor)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="receber" className="space-y-6 mt-4">
            <FinanceiroTable
              filtroTipo="entrada"
              filtrosDraft={filtrosDraft}
              setFiltrosDraft={setFiltrosDraft}
              filtrosAplicados={filtrosAplicados}
              setFiltrosAplicados={setFiltrosAplicados}
              filtrosDraftDefault={filtrosDraftDefault}
            />
          </TabsContent>
          <TabsContent value="pagar" className="space-y-6 mt-4">
            <FinanceiroTable
              filtroTipo="saida"
              filtrosDraft={filtrosDraft}
              setFiltrosDraft={setFiltrosDraft}
              filtrosAplicados={filtrosAplicados}
              setFiltrosAplicados={setFiltrosAplicados}
              filtrosDraftDefault={filtrosDraftDefault}
            />
          </TabsContent>
        </Tabs>
      ) : (
        <>
          <div className="mt-4">
            <ProximosVencimentos vendedorId={vendedorIdEscopo} />
          </div>
          <div className="space-y-6 mt-4">
            <FinanceiroTable
              filtroTipo="entrada"
              filtrosDraft={filtrosDraft}
              setFiltrosDraft={setFiltrosDraft}
              filtrosAplicados={filtrosAplicados}
              setFiltrosAplicados={setFiltrosAplicados}
              filtrosDraftDefault={filtrosDraftDefault}
            />
          </div>
        </>
      )}
    </div>
  );
}
